<script setup lang="ts">
import { useElementHover } from "@vueuse/core";
import { computed, useTemplateRef } from "vue";
import { useDisplay, useTheme } from "vuetify";
import { VBtn } from "vuetify/components";

const props = withDefaults(
  defineProps<{
    hoverColor?: string;
    borderColor?: string;
    textColor?: string;
    activeColor?: string;
    color?: string;
    borderWidth?: number;
    icon?: string | boolean;
    text?: string;
    active?: boolean;
    prependIcon?: string;
    responsive?: boolean;
  }>(),
  {
    hoverColor: "primary",
    borderColor: "primary",
    textColor: "accent",
    activeColor: "accent",
    borderWidth: 1,
  },
);

const theme = useTheme();
const { xs } = useDisplay();

const slots = defineSlots<{
  default?(): unknown;
}>();

const btnRef = useTemplateRef<InstanceType<typeof VBtn>>("k-btn");
const isHovering = useElementHover(computed(() => btnRef.value?.$el));

const btnBorderColor = computed(() => {
  const colors = theme.current.value.colors;
  const color = props.color || props.borderColor;
  return colors[color] || color;
});

const btnActiveColor = computed(() => props.color || props.activeColor);

const btnHoverColor = computed(() => props.color || props.hoverColor);

const btnTextColor = computed(() => props.color || props.textColor);
</script>

<template>
  <VBtn
    ref="k-btn"
    :variant="isHovering || active ? 'flat' : 'text'"
    :icon
    :color="active ? btnActiveColor : isHovering ? btnHoverColor : 'primary'"
    class="k-btn"
    :class="{
      'w-100': responsive && xs,
    }"
    :style="{
      border: `${borderWidth}px solid ${active || isHovering ? 'rgba(0,0,0,0)' : btnBorderColor}`,
    }"
  >
    <template v-if="prependIcon" #prepend>
      <VIcon :icon="prependIcon" />
    </template>

    <template v-if="slots.default || text || icon" #default>
      <VIcon
        v-if="icon && typeof icon === 'string'"
        :icon
        :size="20"
        :color="isHovering || active ? 'white' : btnTextColor"
      />
      <div v-else class="d-flex align-center">
        <slot name="default">
          {{ text }}
        </slot>
      </div>
    </template>
  </VBtn>
</template>

<style scoped lang="scss">
@use "@/styles/mixins/transitions";

.k-btn {
  @include transitions.k-default-transition;
  transition-property:
    box-shadow, transform, opacity, background, border-left-color,
    border-right-color, border-top-color, border-bottom-color;

  :deep(.v-btn__overlay) {
    left: -2px;
    top: -2px;
    width: calc(100% + 4px);
    height: calc(100% + 4px);
  }
}
</style>
